import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import heroTravel from "@/assets/hero-travel.jpg";
import heroEducation from "@/assets/hero-education.jpg";
import heroHealthcare from "@/assets/hero-healthcare.jpg";
import { COPY } from "@/content/copy";
import { useWaitlist, type SignupResult } from "@/hooks/useWaitlist";

type VerticalKey = "travel" | "education" | "healthcare";

const BG: Record<VerticalKey, string> = {
  travel: heroTravel,
  education: heroEducation,
  healthcare: heroHealthcare,
};

const nextKey = (k: VerticalKey): VerticalKey =>
  k === "travel" ? "education" : k === "education" ? "healthcare" : "travel";

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<VerticalKey>("education");
  const [prev, setPrev] = useState<VerticalKey>("education");
  const [fading, setFading] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useWaitlist();

  // Respect user's "reduce motion"
  const reduceMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Optional: gentle auto-rotate every 7s
  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (reduceMotion) return;
    intervalRef.current = window.setInterval(() => {
      show(nextKey(active));
    }, 7000);
    return () => intervalRef.current && window.clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduceMotion]);

  // Trigger a smooth crossfade between prev -> active
  const show = (k: VerticalKey) => {
    if (k === active) return;
    setPrev(active);
    setActive(k);
    setFading(true);
    // Flip to the "final" state on the next frame so CSS transitions animate
    setTimeout(() => setFading(false), 20);
  };

  // Navigate to vertical marketplace
  const navigateToVertical = (vertical: VerticalKey) => {
    navigate(`/${vertical}`);
  };

  // Scroll to How It Works section
  const scrollToHowItWorks = () => {
    const howItWorksSection = document.getElementById('how-it-works');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // Handle waitlist form submission
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOk(email)) {
      toast.warning("Please check your email", {
        description: "That doesn't look like a valid email address.",
      });
      return;
    }
    setLoading(true);
    const res = await signup(email, {
      source: "hero",
      path: window.location.pathname,
      userAgent: navigator.userAgent,
    });
    setLoading(false);

    if (res.ok) {
      if (res.status === "created") {
        toast.success("🎉 Welcome to the Flexaura Waitlist!", {
          description: "Thank you for joining! We've received your email and added you to our exclusive early access list. You'll be the first to know when we launch in your region.",
          duration: 6000,
        });
        setEmail("");
      } else {
        toast.success("✅ You're already registered!", {
          description: "Great news! You're already on our waitlist. We'll keep you updated with all the latest developments and early access opportunities.",
          duration: 5000,
        });
      }
    } else {
      toast.error("Oops! Something went wrong", {
        description: "We couldn't add you to the waitlist right now. Please try again in a moment or contact us at hello@flexaura.me",
        duration: 5000,
      });
    }
  };

  return (
    <section className="relative overflow-hidden">
      {/* Base brand gradient (teal/purple) */}
      <div className="absolute inset-0 bg-brand-gradient" aria-hidden="true" />

      {/* Two-layer crossfade: prev (fades out) + active (fades in) */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-center bg-cover transition-opacity duration-[3000ms] ease-in-out mix-blend-luminosity ${
          fading ? "opacity-35" : "opacity-0"
        }`}
        style={{ backgroundImage: `url(${BG[prev]})` }}
      />
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-center bg-cover transition-opacity duration-[3000ms] ease-in-out mix-blend-luminosity ${
          fading ? "opacity-0" : "opacity-35"
        }`}
        style={{ backgroundImage: `url(${BG[active]})` }}
      />

      {/* Brand wash for consistent Flexaura tint + contrast */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-accent/30 via-primary/20 to-black/15"
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center text-white">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight">
            Finance life's biggest milestones your way
          </h1>

          <p className="mt-6 mx-auto max-w-3xl text-lg md:text-xl text-white/90">
            From dream holidays to tuition fees and essential healthcare, Flexaura turns large costs into simple monthly plans with every financing option in one trusted marketplace.
          </p>

          {/* CTA */}
          <form onSubmit={onSubmit} className="mt-10 md:mt-12 flex items-center justify-center gap-3 md:gap-4 flex-col sm:flex-row">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email for early access"
              className="w-full sm:w-80 h-12 rounded-xl bg-white/15 backdrop-blur-md placeholder-white/70 text-white px-4 outline-none ring-1 ring-white/25 focus:ring-white/40"
              aria-label="Email address"
              required
            />
            <button
              className="btn-primary h-12 px-6 rounded-xl text-white font-semibold shadow-brand disabled:opacity-60"
              type="submit"
              disabled={loading}
            >
              {loading ? "Joining…" : "👉 Join Early Access"}
            </button>
            <button
              className="h-11 px-5 rounded-xl bg-white/12 hover:bg-white/15 text-white/85 ring-1 ring-white/20"
              type="button"
              onClick={scrollToHowItWorks}
            >
              How it Works
            </button>
          </form>

          {/* Vertical chips */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {(["travel", "education", "healthcare"] as VerticalKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => navigateToVertical(k)}
                onMouseEnter={() => show(k)}
                className={`rounded-full px-3 py-1 text-xs md:text-sm ring-1 ring-white/20 transition-colors hover:bg-white/15 ${
                  active === k && !fading ? "bg-white/20 text-white" : "bg-white/10 text-white/80"
                }`}
              >
                {k[0].toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>

          {/* Micro badges */}
          <div className="mt-4 flex items-center justify-center gap-3 text-xs md:text-sm text-white/75">
            <span className="rounded-full px-3 py-1 ring-1 ring-white/20 bg-white/5">AED 2K–200K+</span>
            <span className="rounded-full px-3 py-1 ring-1 ring-white/20 bg-white/5">Flexible Terms</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;